/* global window, t */
// Task-byggere for agentene som mangler i den opprinnelige AI Regnskapsfører-lista:
// faktura, purring, innboks, avstemming, kontroll, frister og kjørebok.
//
// Samme kontrakt som resten av read-modellen: alt er avledet fra eksisterende
// backend-modeller, ingen av dem muterer noe, og ingen av dem oppgir en
// sikkerhetsprosent de ikke har dekning for. Handlingen er å åpne dypflaten.
//
// Ligger i egen fil fordi `autopilot-model.jsx` allerede nærmet seg
// filstørrelsesgrensen i AGENTS.md.
const apAgentHelpers = () => window.apTaskHelpers || {};
const apAgentDate = (value) => {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};
// Datoene i UI-modellene er norske (`dd.mm.yyyy`), mens backend-feltene er ISO.
const apAgentParseDate = (value) => {
  if (!value) return null;
  const text = String(value);
  const nb = /^(\d{2})\.(\d{2})\.(\d{4})$/.exec(text);
  const iso = /^(\d{4})-(\d{2})-(\d{2})/.exec(text);
  if (nb) return apAgentDate(new Date(Number(nb[3]), Number(nb[2]) - 1, Number(nb[1])));
  if (iso) return apAgentDate(new Date(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3])));
  return apAgentDate(text);
};
const apAgentDaysBetween = (from, to) => (Date.UTC(to.getFullYear(), to.getMonth(), to.getDate()) - Date.UTC(from.getFullYear(), from.getMonth(), from.getDate())) / 86400000;
// Tall skal folge brukerens sprakvalg, ikke last til nb-NO. Se i18n-regelen i AGENTS.md.
const apAgentAmount = (value) => {
  const rounded = Math.round(Number(value) || 0);
  return window.PlummeI18n?.number ? window.PlummeI18n.number(rounded) : String(rounded);
};
const apAgentYearOf = (value) => {
  const date = apAgentParseDate(value);
  return date ? date.getFullYear() : null;
};
const apAgentArray = (value) => Array.isArray(value) ? value : [];
const apAgentNumber = (value) => Number(value) || 0;
const apAgentImportedOpenItemAmount = (item) =>
  apAgentNumber(item?.remainingAmount ?? item?.outstanding ?? item?.amountGross ?? item?.amount);
const apAgentImportedOutstandingSplit = (state, statsKey, openItemsKey, today) => {
  const statsEntries = apAgentArray(state?.accountingImportPartyStats?.[statsKey]);
  const originalStatsTotal = statsEntries
    .reduce((sum, entry) => sum + Math.max(0, Math.round(apAgentNumber(entry?.outstanding))), 0);
  const openItems = apAgentArray(state?.accountingImportOpenItems?.[openItemsKey])
    .map((item) => window.PlummeAccountingImportOpenItems?.decorate?.(state, item) || item);
  const settledTotal = openItems.reduce((sum, item) =>
    sum + Math.max(0, apAgentNumber(item.originalRemainingAmount) - Math.max(0, apAgentNumber(item.remainingAmount))), 0);
  const statsTotal = Math.max(0, originalStatsTotal - settledTotal);
  const split = openItems.reduce((totals, item) => {
    const amount = Math.round(apAgentImportedOpenItemAmount(item));
    const due = apAgentParseDate(item.dueDate || item.date);
    if (due && due < today) totals.overdue += amount;
    else totals.current += amount;
    return totals;
  }, { current: 0, overdue: 0 });
  const detailCurrent = Math.max(0, split.current);
  const detailOverdue = Math.max(0, split.overdue);
  const detailTotal = detailCurrent + detailOverdue;
  if (!statsEntries.length) return { current: Math.round(detailCurrent), overdue: Math.round(detailOverdue) };
  if (statsTotal <= 0 || detailTotal <= 0) return { current: Math.round(statsTotal), overdue: 0 };
  if (detailTotal > statsTotal) {
    const overdue = Math.round(statsTotal * (detailOverdue / detailTotal));
    return { current: statsTotal - overdue, overdue };
  }
  const overdue = Math.min(statsTotal, Math.round(detailOverdue));
  return { current: statsTotal - overdue, overdue };
};
// Datofilteret ligger i `autopilot-periods.jsx`, slik at agentene og fargene på
// månedsknappene ikke kan bli uenige om hva «januar» betyr. `month == null`
// betyr hele året.
const apAgentInPeriod = (value, year, month) => Boolean(window.apPeriodIn?.(value, year, month));
// Måned skrevet ut i en tittel: «januar 2026» eller «2026» når hele året vises.
const apAgentPeriodLabel = (year, month) =>
  month == null ? String(year) : `${AP_AGENT_MONTHS[month]} ${year}`;
// I en tittel skal perioden leses som en periode, ikke som en del av setningen.
// `**...**` plukkes opp av `apRichText()` i autopilot.jsx og blir <strong>.
// Brødtekst (`desc`) bruker den vanlige varianten uten utheving.
const apAgentPeriodStrong = (year, month) => `**${apAgentPeriodLabel(year, month)}**`;
// Maaneden er fort i et annet system naar den ligger for overtakelsesdatoen.
// Da har vi ingenting aa uttale oss om, verken positivt eller negativt.
// Tre kilder til «dette er gjort opp et annet sted», og de gjelder samtidig:
//
//   - maaneden ligger for overtakelsesdatoen i aaret
//   - hele aaret er levert (aarsavslutning, skattemelding og MVA bekreftet)
//   - `bankOnly`: banken er avstemt til og med denne maaneden, ogsaa i et
//     tidligere aar. Bankdatoen er fasit for bankarbeidet bakover.
//
// `state` og `year` er valgfrie for aa holde de gamle kallene i live.
const apAgentImportedPeriod = (month, transitionMonth, { state, year, bankOnly = false } = {}) => {
  if (state && year != null && window.apYearSettled?.(state, year)) return true;
  if (bankOnly && state && year != null && month != null
    && window.apBankMonthSettled?.(state, year, month)) return true;
  return month != null && transitionMonth != null && Number(month) < Number(transitionMonth);
};
// ---------------------------------------- foer bankavstemmingen i det hele tatt
//
// «Fort i et annet system» er en paastand om at NOEN ANDRE gjorde jobben. Den
// krever en overtakelse. Ligger maaneden bare foer datoen brukeren selv satte
// som inngaaende saldo paa bankkontoen, er sannheten en annen og enklere: Plumme
// avstemmer ikke saa langt tilbake, og har ingen utskrift aa etterlyse.
const apAgentBeforeBankCoverage = (month, { state, year } = {}) =>
  Boolean(state) && year != null && month != null
  && Boolean(window.apBankMonthBeforeCoverage?.(state, year, month));

const apAgentBankNotStartedTask = ({ apTask }, { id, icon, kind, month, year, from }) => apTask({
  id,
  icon,
  title: t('ap.agent.beforeBank.title', 'Bankavstemmingen starter senere enn {{month}}', { month: apAgentMonthStrong(month, year) }),
  desc: from
    ? t('ap.agent.beforeBank.desc', 'Bankkontoen har inngående saldo {{date}}, og Plumme avstemmer fra dagen etter.', { date: apAgentDateLabel(from) })
    : t('ap.agent.beforeBank.descNoDate', 'Bankkontoen avstemmes først fra en senere dato.'),
  detail: t('ap.agent.beforeBank.detail', 'Datoen står på bankkontoen i Innstillinger. Skal Plumme avstemme denne måneden også, må startdatoen settes tidligere og kontoutskriften lastes opp i Bank.'),
  status: 'idle',
  route: 'bank',
  actionLabel: t('ap.agent.beforeBank.action', 'Åpne bank'),
  kind,
  scope: 'month',
});

// En agent som ikke eier perioden skal si nettopp det, ikke melde alt i orden.
const apAgentArchiveTask = ({ apTask }, { id, icon, kind, route, month, year }) => apTask({
  id,
  icon,
  title: t('ap.agent.imported.title', '{{month}} er ført i et annet system', { month: apAgentMonthStrong(month, year) }),
  desc: t('ap.agent.imported.desc', 'Regnskapet ble overtatt senere. AI Regnskapsfører har ikke kontrollert denne måneden.'),
  detail: t('ap.agent.imported.detail', 'Tall og bilag for perioden ligger i importarkivet. De må kontrolleres mot det gamle systemet, ikke mot Plumme.'),
  status: 'idle',
  route,
  actionLabel: t('ap.agent.imported.action', 'Åpne arkivet'),
  kind,
  scope: 'month',
});

// ------------------------------------------------------- rekkefolge paa arbeid
//
// Bank foerst. Bilag kan ikke kobles mot banklinjer som ikke finnes, og en
// maaned kan ikke avstemmes mot en utskrift som ikke er lastet opp. Uten denne
// sperren meldte agentene «ingen banklinjer» og «ingenting aa koble» — som leses
// som at alt er i orden, naar sannheten er at grunnlaget mangler.
const apAgentStatementCovers = (statement, year, month) => {
  const from = new Date(statement?.periodFrom);
  const to = new Date(statement?.periodTo || statement?.periodFrom);
  if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime())) return false;
  if (month == null) return from.getFullYear() === year || to.getFullYear() === year;
  return from <= new Date(year, Number(month) + 1, 0) && to >= new Date(year, Number(month), 1);
};
const apAgentBankCovered = (bankModel, year, month) =>
  (window.apTaskHelpers?.apArray?.(bankModel?.statements) || [])
    .some((statement) => statement?.mode !== 'private' && statement?.mode !== 'privat' && apAgentStatementCovers(statement, year, month));
// Hvilke kontoer som mangler utskrift for perioden. Et foretak kan ha flere
// kontoer som skal avstemmes, og «maaneden er ikke avstemt» sier ingenting om
// hvilken av dem det gjelder. Drift og skattetrekk maa alltid avstemmes; resten
// bare naar brukeren har huket av for det.
const AP_AGENT_MUST_RECONCILE = ['operating', 'tax_withholding'];
const apAgentReconcileAccounts = (state) =>
  window.apBankReconcileAccounts
    ? window.apBankReconcileAccounts(state)
    : (window.apTaskHelpers?.apArray?.(state?.settings?.bankAccounts) || [])
      .filter((account) => account?.active !== false)
      .filter((account) => AP_AGENT_MUST_RECONCILE.includes(String(account?.kind || 'operating')) || account?.reconcile === true);
const apAgentHasBankReconciliationScope = (state) =>
  window.apHasBankReconciliationScope
    ? window.apHasBankReconciliationScope(state)
    : (
      !(window.apTaskHelpers?.apArray?.(state?.settings?.bankAccounts) || []).filter((account) => account?.active !== false).length ||
      apAgentReconcileAccounts(state).length > 0);
const apAgentAccountsMissingStatement = (state, bankModel, year, month) => {
  const covered = (window.apTaskHelpers?.apArray?.(bankModel?.statements) || [])
    .filter((statement) => statement?.mode !== 'private' && statement?.mode !== 'privat' && apAgentStatementCovers(statement, year, month))
    .map((statement) => String(statement?.bankAccountId || ''));
  return apAgentReconcileAccounts(state).filter((account) => window.apBankAccountInMonthScope?.(account, year, month) !== false)
    .filter((account) => {
      const accountIds = account?._apBankAccountIds?.length
        ? account._apBankAccountIds
        : [String(account?.id || '')];
      return !accountIds.some((id) => covered.includes(String(id)));
    });
};

// En oppgave som venter paa bankutskriften. `blocked` gir raden en Aapne-knapp
// til Bank i stedet for en godkjenning brukeren ikke har grunnlag for aa gi.
const apAgentAwaitingStatement = ({ apTask, apLine }, { id, icon, kind, month, year, accounts = [] }) => apTask({
  id,
  icon,
  title: t('ap.agent.awaitBank.title', 'Venter på bankutskrift for {{month}}', { month: apAgentMonthStrong(month, year) }),
  desc: accounts.length > 1
    ? t('ap.agent.awaitBank.descAccounts', '{{count}} kontoer mangler kontoutskrift for perioden.', { count: accounts.length })
    : t('ap.agent.awaitBank.desc', 'Last opp kontoutskriften for perioden. Uten den finnes det ingen banklinjer å koble bilag mot eller avstemme.'),
  detail: t('ap.agent.awaitBank.detail', 'Bankarbeidet går i rekkefølge: utskriften må inn før bilagene kan kobles og måneden kan avstemmes.'),
  status: 'blocked',
  route: 'bank',
  actionLabel: t('ap.agent.awaitBank.action', 'Åpne bank'),
  blockedReason: accounts.length > 1
    ? t('ap.agent.awaitBank.reasonAccounts', 'Mangler utskrift på {{count}} kontoer', { count: accounts.length })
    : t('ap.agent.awaitBank.reason', 'Mangler bankutskrift for perioden'),
  // En rad per konto: «måneden er ikke avstemt» sier ingenting om hvilken konto
  // det gjelder naar foretaket har bade drifts- og skattetrekkskonto.
  lines: accounts.map((account) => apLine({
    id: `await-statement-${account.id}`,
    title: account.name || t('ap.agent.awaitBank.account', 'Bankkonto'),
    sub: [account.accountNumber, account.kind === 'tax_withholding' ? t('set.bank.typeTax', 'Skattetrekk') : '']
      .filter(Boolean).join(' · ') || t('ap.agent.awaitBank.reason', 'Mangler bankutskrift for perioden'),
    status: 'mangler',
    route: 'bank',
  })),
  kind,
  scope: 'month',
});

// «2026-08-01» som «1. august 2026». Datoen kommer fra bankkontoens
// inngaaende saldo, og skal leses som en dato i en setning.
const apAgentDateLabel = (iso) => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(iso || ''));
  if (!match) return String(iso || '');
  const month = AP_AGENT_MONTHS[Number(match[2]) - 1];
  return month ? `${Number(match[3])}. ${month} ${match[1]}` : String(iso);
};

// Samme, men med stor forbokstav — brukes der måneden starter en setning.
const apAgentMonthLabel = (month, year) => {
  const name = AP_AGENT_MONTHS[month];
  return name ? `${name.slice(0, 1).toUpperCase()}${name.slice(1)} ${year}` : String(year);
};
const apAgentMonthStrong = (month, year) => `**${apAgentMonthLabel(month, year)}**`;

const AP_AGENT_MONTHS = [
  'januar', 'februar', 'mars', 'april', 'mai', 'juni',
  'juli', 'august', 'september', 'oktober', 'november', 'desember',
];

const apAgentPeriodKey = (year, month) => `${year}-${String((month == null ? new Date().getMonth() : month) + 1).padStart(2, '0')}`;
const apAgentItemIgnored = (state, taskId, itemId, year, month) =>
  Boolean(state?.autopilotPeriodIgnores?.[`${taskId}:${itemId}@${apAgentPeriodKey(year, month)}`]);

// ---------------------------------------------------------------- faktura

// One worklist owns unsent invoices and offers; sending still uses the existing services.
const apInvoiceTask = ({ state, invoiceModel, year, month }) => {
  const { apTask, apLine, apArray, apTaskStatus } = apAgentHelpers();
  const rows = apArray(invoiceModel?.rows)
    .filter((row) => ['faktura', 'tilbud'].includes(row.type) && apAgentYearOf(row.date) === year)
    .filter((row) => !row.archivedAt && !row._backend?.archivedAt && !row.importedReadOnly);
  const pending = rows.filter((row) => {
    if (window.apAutopilotItemHidden?.(state, 'faktura', row.id, year, month)) return false;
    if (row.sentAt || ['kreditert', 'annullert', 'annulled', 'deleted', 'reversed'].includes(row.status)) return false;
    if (row.type === 'tilbud') return !row.offerAcceptedAt && !row._backend?.offerAcceptedAt && row.status !== 'akseptert';
    return !(['betalt', 'paid'].includes(row.status) || row.paymentStatus === 'paid' || row.paidAt
      || (row.remainingAmount != null && Number.isFinite(Number(row.remainingAmount)) && Number(row.remainingAmount) <= 0.005));
  });
  const unsent = pending.filter((row) => row.type === 'faktura' && !row.draft && row.status !== 'utkast');
  return apTask({
    id: 'faktura', icon: 'fileText', kind: 'faktura', scope: 'running', route: 'fakturaliste',
    title: t('ap.agent.sales.title', 'Fakturaer og tilbud'),
    desc: t('ap.agent.sales.pending', '{{count}} dokumenter er ikke sendt.', { count: pending.length }),
    detail: t('ap.agent.sales.detail', 'Send ferdige fakturaer og tilbud. Utkast må ferdigstilles i fakturalisten.'),
    status: apTaskStatus({ reviewCount: pending.length, done: pending.length === 0, checked: rows.length }),
    actionLabel: t('ap.agent.invoice.action', 'Åpne fakturalisten'),
    reviewCount: pending.length,
    lines: pending.map((row) => apLine({
      id: row.id, date: row.date, title: row.customer,
      sub: [row.num, row.draft || row.status === 'utkast' ? t('ap.agent.sales.draft', 'Utkast') : t('ap.agent.invoice.lineUnsent', 'Ferdig, men ikke sendt')].filter(Boolean).join(' · '),
      amount: row.amount, status: 'kontroll', route: 'fakturaliste',
      data: { documentType: row.type, draft: Boolean(row.draft || row.status === 'utkast') },
    })),
    execution: unsent.length ? {
      action: 'send-unsent-invoices', count: unsent.length, invoiceIds: unsent.map((row) => row.id),
      label: t('ap.agent.invoice.execute', 'Send {{count}} fakturaer', { count: unsent.length }),
      doneLabel: t('ap.agent.invoice.executeDone', 'fakturaer sendt'),
    } : null,
  });
};

// ---------------------------------------------------------------- purring

// Sendte fakturaer som har passert forfall. Sorteres eldst først — det er den
// rekkefølgen purring faktisk skal skje i.
// NB: purring er bevisst ikke månedsfiltrert. En faktura som forfalt i mars og
// fortsatt er ubetalt er penger som står ute nå, ikke et marsproblem. Den er
// `scope: 'running'` og vises derfor uansett hvilken måned brukeren står i.
const apDunningTask = ({ invoiceModel, today, year, month, state }) => {
  const { apTask, apLine, apArray, apNumber, apTaskStatus, AP_LINE_CAP = 500 } = apAgentHelpers();
  // Aapne kundefakturaer som fulgte med regnskapsimporten. De ligger ikke i
  // fakturalisten, og Plumme kan verken sende eller purre dem — men de er ekte
  // penger som staar ute, og aa tie om dem ville vaert verre enn aa vise dem.
  const importedOverdue = apArray(state?.accountingImportOpenItems?.customerInvoices)
    .filter((item) => apNumber(item.remainingAmount) > 0)
    .map((item) => ({ item, due: apAgentParseDate(item.dueDate || item.date) }))
    .filter((entry) => entry.due && entry.due < today)
    .sort((a, b) => a.due - b.due);
  const overdue = apArray(invoiceModel?.rows)
    .filter((row) => row.type === 'faktura' && apAgentYearOf(row.date) === year && row.sentAt && apNumber(row.remainingAmount) > 0)
    .filter((row) => !apAgentItemIgnored(state, 'purring', row.id, year, month))
    .map((row) => ({ row, due: apAgentParseDate(row.due) }))
    .filter((entry) => entry.due && entry.due < today)
    .map((entry) => ({ ...entry, days: apAgentDaysBetween(entry.due, today) }))
    .sort((a, b) => b.days - a.days);
  const total = overdue.reduce((sum, entry) => sum + apNumber(entry.row.remainingAmount), 0);
  const importedTotal = apAgentImportedOutstandingSplit(state, 'customers', 'customerInvoices', today).overdue;
  const allCount = overdue.length + importedOverdue.length;
  return apTask({
    id: 'purring',
    icon: 'clock',
    title: allCount
      ? t('ap.agent.dunning.title', '{{count}} forfalt faktura(er) venter på purring', { count: allCount })
      : t('ap.agent.dunning.titleDone', 'Ingen forfalte fakturaer'),
    desc: allCount
      ? t('ap.agent.dunning.desc', '{{amount}} kr står utestående etter forfall.', { amount: apAgentAmount(total + importedTotal) })
      : t('ap.agent.dunning.descDone', 'Alle sendte fakturaer er innenfor forfall eller betalt.'),
    detail: importedOverdue.length
      ? t('ap.agent.dunning.detailImported', '{{count}} av postene kommer fra regnskapsimporten og ligger ikke i fakturalisten. Plumme kan verken sende eller purre dem — de må følges opp i det gamle systemet, eller registreres som oppgjort her.', { count: importedOverdue.length })
      : t('ap.agent.dunning.detail', 'Purring, purregebyr og forsinkelsesrente sendes fra fakturalisten. AI Regnskapsfører sender ingenting selv.'),
    status: apTaskStatus({ reviewCount: allCount, done: allCount === 0, checked: allCount || apArray(invoiceModel?.rows).length }),
    route: 'fakturaliste',
    actionLabel: t('ap.agent.dunning.action', 'Åpne fakturalisten'),
    reviewCount: allCount,
    lines: [...importedOverdue.slice(0, 4).map(({ item, due }) => apLine({
      id: item.id,
      date: item.dueDate || item.date || '',
      title: item.partyName || t('ap.agent.dunning.importedParty', 'Kunde fra importen'),
      sub: t('ap.agent.dunning.lineImported', 'Fra regnskapsimporten · faktura {{num}} · kan ikke purres fra Plumme', { num: item.invoiceNumber || '-' }),
      amount: apNumber(item.remainingAmount),
      status: 'avvik',
      route: 'kunder',
    })), ...overdue.slice(0, AP_LINE_CAP).map(({ row, days }) => apLine({
      id: row.id,
      date: row.due,
      title: row.customer,
      sub: t('ap.agent.dunning.line', 'Forfalt for {{days}} dag(er) siden · faktura {{num}}', { days, num: row.num || '-' }),
      amount: apNumber(row.remainingAmount),
      status: days > 30 ? 'avvik' : 'kontroll',
      route: 'fakturaliste',
    }))],
    // Bare de ekte Plumme-fakturaene kan faktisk purres herfra.
    execution: overdue.length ? {
      action: 'send-payment-reminders',
      count: overdue.length,
      invoiceIds: overdue.map(({ row }) => row.id),
      label: t('ap.agent.dunning.execute', 'Send {{count}} påminnelser', { count: overdue.length }),
      confirmTitle: t('ap.agent.dunning.executeTitle', 'Send betalingspåminnelser'),
      confirmWarn: t('ap.agent.dunning.executeWarn', 'Påminnelsene går rett til kundene og kan ikke trekkes tilbake. Kunder som nettopp har betalt uten at betalingen er registrert, vil også få en.'),
      checkedNote: t('ap.agent.dunning.executeNote', 'Plumme purrer ikke samme faktura oftere enn hver sjuende dag, og hopper over fakturaer som er gjort opp eller mangler kunde-e-post.'),
      doneLabel: t('ap.agent.dunning.executeDone', 'påminnelser sendt'),
    } : null,
    kind: 'purring',
    scope: 'running',
  });
};

// ---------------------------------------------------------------- innboks

// Bilag som er kommet inn via e-post/opplasting og lest av OCR-en, men ikke
// bokført. Skiller på hva OCR-en faktisk fikk til: klar, må kontrolleres, leser.
const apInboxTask = ({ state, inboxModel, year, month, transitionMonth, transitionDate }) => {
  const helpers = apAgentHelpers();
  const { apTask, apLine, apArray, apNumber, apTaskStatus, AP_LINE_CAP = 500 } = helpers;
  if (apAgentImportedPeriod(month, transitionMonth, { state, year })) {
    return apAgentArchiveTask(helpers, { id: 'innboks', icon: 'receipt', kind: 'innboks', route: 'innboks', month, year });
  }
  // Bilaget hører til perioden det gjelder. OCR-en gir `date` når den fikk lest
  // datoen; ellers er `received` (mottatt) det beste vi har.
  const inPeriod = (entry) => {
    const documentDate = entry.date || entry.receiptAnalysis?.receiptDate;
    if (month != null && !documentDate) return false;
    return apAgentInPeriod(documentDate || entry.received, year, month);
  };
  // Agenten skal telle nyaktig det maanedskontrollen viser. Et bilag som er
  // koblet til en banklinje og ikke trenger kontroll eies av den linjen — telles
  // det her ogsaa, ber maaneden om et arbeid arbeidsflaten ikke tilbyr.
  const ownRow = (entry) => inPeriod(entry) && !entry?.bankLinkedResolved;
  const ready = apArray(inboxModel?.ready).filter(ownRow);
  const needsReview = apArray(inboxModel?.needsReview).filter(ownRow);
  const analyzing = apArray(inboxModel?.analyzing).filter(ownRow);
  const pending = [...ready, ...needsReview];
  // Et bilag datert for overtakelsen er trolig allerede bokfort i det gamle
  // systemet og fulgte med i importen. Bokfores det her ogsaa, staar kjopet to
  // ganger. Slike bilag tilbys derfor ikke for samlet bokforing.
  const beforeTakeover = (entry) => {
    if (!transitionDate) return false;
    const raw = entry.date || entry.received;
    if (!raw) return false;
    const date = new Date(raw);
    return !Number.isNaN(date.getTime()) && date < new Date(transitionDate);
  };
  const historic = ready.filter(beforeTakeover);
  return apTask({
    id: 'innboks',
    icon: 'receipt',
    title: pending.length
      ? t('ap.agent.inbox.title', '{{count}} bilag i innboksen er ikke bokført', { count: pending.length })
      : t('ap.agent.inbox.titleDone', 'Innboksen er tom'),
    desc: pending.length
      ? t('ap.agent.inbox.desc', '{{ready}} er klare for bokføring og {{review}} må kontrolleres.', { ready: ready.length, review: needsReview.length })
      : analyzing.length
      ? t('ap.agent.inbox.descAnalyzing', '{{count}} bilag leses fortsatt.', { count: analyzing.length })
      : t('ap.agent.inbox.descDone', 'Ingen bilag venter på behandling.'),
    detail: historic.length
      ? t('ap.agent.inbox.detailHistoric', '{{count}} av bilagene er datert før regnskapet ble overtatt. De er trolig allerede bokført i det gamle systemet, og bokføres ikke herfra — de må kobles som dokumentasjon.', { count: historic.length })
      : t('ap.agent.inbox.detail', 'Bilag leses automatisk når de kommer inn. Bokføring og kontovalg godkjennes i innboksen.'),
    status: apTaskStatus({ readyCount: ready.length, reviewCount: needsReview.length, done: pending.length === 0, checked: pending.length + analyzing.length }),
    route: 'innboks',
    actionLabel: t('ap.agent.inbox.action', 'Åpne innboksen'),
    readyCount: ready.length,
    reviewCount: needsReview.length,
    // Selve bokføringen eies av den backend-validerte køen. Denne read-modellen
    // skal aldri tilby en parallell skrivevei for de samme bilagene.
    execution: null,
    lines: pending.slice(0, AP_LINE_CAP).map((entry) => apLine({
      id: entry.id,
      date: entry.received || '',
      title: entry.subject || entry.name || t('ap.agent.inbox.voucher', 'Bilag'),
      // `reasons` er OCR-ens egne kontrollgrunner ("Mangler dato", "Lav sikkerhet
      // pa analysen"). De er mer presise enn noe vi kunne formulert her.
      sub: entry.review?.status === 'ready-to-book'
        ? t('ap.agent.inbox.lineReady', 'Lest og klar for bokføring')
        : apArray(entry.review?.reasons).join(' ') || t('ap.agent.inbox.lineReview', 'Må kontrolleres før bokføring'),
      amount: apNumber(entry.sum),
      status: entry.review?.status === 'ready-to-book' ? 'matchet' : 'kontroll',
      route: 'innboks',
    })),
    kind: 'innboks',
    scope: 'month',
  });
};

// ------------------------------------------------------------ avstemming

// Måneder som har gått uten at driftsbanken er avstemt og bokført. Dette er
// noe annet enn banktasken, som teller enkeltlinjer i utskriftene du har lastet
// opp: her er spørsmålet om måneden i det hele tatt er gjort opp.
const apReconciliationTask = ({ state, reconciledMonths, bankModel, year, today, month, transitionMonth }) => {
  const bankIntegrityCount = bankModel?.bankControl?.year === year && window.PlummeBankPeriodControl ? (month == null ? bankModel.bankControl.count : window.PlummeBankPeriodControl.monthIssues(bankModel.bankControl, month)) : 0; if (bankIntegrityCount) return apAgentHelpers().apTask({ id: 'avstemming', icon: 'checkCircle', title: t('bank.control.needsReview'), desc: t('bank.control.needsReview'), status: 'needs_review', reviewCount: bankIntegrityCount, bankIntegrityIssue: true, route: 'bank', actionLabel: t('ap.agent.reconcile.action'), kind: 'avstemming', scope: 'month' });
  const helpers = apAgentHelpers();
  const { apTask, apLine, apArray, apTaskStatus, AP_LINE_CAP = 500 } = helpers;
  // Banken for overtakelsen ble avstemt i det gamle systemet. Plumme har ingen
  // utskrift, og «ikke avstemt» ville vaert en anklage for noe som er gjort.
  if (apAgentImportedPeriod(month, transitionMonth, { state, year, bankOnly: true })) {
    return apAgentArchiveTask(helpers, { id: 'avstemming', icon: 'checkCircle', kind: 'avstemming', route: 'bank', month, year });
  }
  if (apAgentBeforeBankCoverage(month, { state, year })) {
    return apAgentBankNotStartedTask(helpers, { id: 'avstemming', icon: 'checkCircle', kind: 'avstemming', month, year, from: window.apBankCoverageFrom?.(state) });
  }
  if (!apAgentHasBankReconciliationScope(state)) {
    return apTask({
      id: 'avstemming',
      icon: 'checkCircle',
      title: t('ap.agent.reconcile.disabledTitle', 'Avstemming er ikke aktiv'),
      desc: t('ap.agent.reconcile.disabledDesc', 'Ingen aktive bankkontoer er satt til avstemming.'),
      detail: t('ap.agent.reconcile.disabledDetail', 'Dette passer for foretak som bruker privat konto og har valgt at kontoen ikke skal avstemmes i Bank.'),
      status: apTaskStatus(),
      route: 'bank',
      actionLabel: t('ap.agent.reconcile.action', 'Åpne bank'),
      kind: 'avstemming',
      scope: 'month',
    });
  }
  const months = apArray(reconciledMonths);
  const lastRelevant = year < today.getFullYear() ? 11 : today.getMonth() - 1;
  const missing = [];
  // Med en valgt måned handler oppgaven om den ene måneden, ikke om året. En
  // måned som ikke er avsluttet ennå kan ikke avstemmes, og teller derfor ikke.
  const from = month == null ? 0 : month;
  const to = month == null ? lastRelevant : Math.min(month, lastRelevant);
  for (let index = from; index <= to; index += 1) {
    if (!months[index]) missing.push(index);
  }
  const total = Math.max(0, to - from + 1);
  const closed = total - missing.length;
  // Maaneden er passert og mangler avstemming — men uten utskrift er «ikke
  // avstemt» feil diagnose. Det som mangler er grunnlaget, og det er en
  // opplasting, ikke en godkjenning.
  // Avstemming maaler per konto, ikke per maaned: maaneden er ikke gjort opp
  // for hver konto som skal avstemmes har sin utskrift. Bilagsmatch noeyer seg
  // med at det finnes EN utskrift — bilag kan kobles mot de linjene som er der.
  const awaitingAccounts = month != null && total > 0 && missing.length
    ? apAgentAccountsMissingStatement(state, bankModel, year, month)
    : [];
  const noStatementAtAll = month != null && total > 0 && missing.length
    && !apAgentReconcileAccounts(state).length && !apAgentBankCovered(bankModel, year, month);
  if (awaitingAccounts.length || noStatementAtAll) {
    return apAgentAwaitingStatement(helpers, { id: 'avstemming', icon: 'checkCircle', kind: 'avstemming', month, year, accounts: awaitingAccounts });
  }
  // Kronologi: inngaaende saldo paa en maaned ER utgaaende saldo fra den forrige.
  // Er en tidligere maaned uavstemt, kan denne ikke avstemmes — og da skal
  // agenten peke paa den maaneden, ikke be om en godkjenning her.
  // Bare naar denne maaneden faktisk kan avstemmes: en maaned som ikke er
  // avsluttet ennaa venter ikke paa noe, den er bare ikke moden.
  const earlierMissing = month != null && total > 0 && missing.length && month > (transitionMonth || 0)
    ? (() => {
      for (let index = (transitionMonth || 0); index < month; index += 1) {
        if (index <= lastRelevant && !months[index]) return index;
      }
      return null;
    })()
    : null;
  if (earlierMissing != null) {
    return apTask({
      id: 'avstemming',
      icon: 'checkCircle',
      title: t('ap.agent.reconcile.titleBlockedOrder', '{{month}} må avstemmes først', { month: apAgentMonthStrong(earlierMissing, year) }),
      desc: t('ap.agent.reconcile.descBlockedOrder', 'Avstemming går kronologisk. Inngående saldo hentes fra forrige måned, så et avvik der ville blitt usynlig her.'),
      detail: t('ap.agent.reconcile.detailBlockedOrder', 'Gå til Bank og gjør ferdig den eldste uavstemte perioden. Da åpner denne måneden seg av seg selv.'),
      status: 'blocked',
      route: 'bank',
      actionLabel: t('ap.agent.reconcile.action', 'Åpne bank'),
      // Liten forbokstav: maaneden staar midt i setningen her, ikke forst.
      blockedReason: t('ap.agent.reconcile.reasonBlockedOrder', 'Venter på {{month}}', { month: apAgentPeriodLabel(year, earlierMissing) }),
      kind: 'avstemming',
      scope: 'month',
    });
  }
  // `total === 0` med en valgt måned betyr at måneden ikke er avsluttet ennå.
  const single = month != null;
  const label = apAgentMonthLabel(month, year);
  const labelStrong = apAgentMonthStrong(month, year);
  return apTask({
    id: 'avstemming',
    icon: 'checkCircle',
    title: single
      ? total === 0
        ? t('ap.agent.reconcile.titleMonthOpen', '{{month}} er ikke avsluttet ennå', { month: labelStrong })
        : missing.length
        ? t('ap.agent.reconcile.titleMonth', '{{month}} er ikke avstemt', { month: labelStrong })
        : t('ap.agent.reconcile.titleMonthDone', '{{month}} er avstemt', { month: labelStrong })
      : missing.length
      ? t('ap.agent.reconcile.title', '{{count}} måned(er) er ikke avstemt', { count: missing.length })
      : t('ap.agent.reconcile.titleDone', 'Alle avsluttede måneder er avstemt'),
    desc: single
      ? total === 0
        ? t('ap.agent.reconcile.descMonthOpen', 'Måneden avstemmes når den er avsluttet og bankutskriften er importert.')
        : missing.length
        ? t('ap.agent.reconcile.descMonth', 'Driftsutskriften for {{month}} er ikke bokført og arkivert i Bank.', { month: label })
        : t('ap.agent.reconcile.descMonthDone', 'Driftsutskriften for {{month}} er bokført og arkivert.', { month: label })
      : lastRelevant < 0
      ? t('ap.agent.reconcile.descEarly', 'Ingen måneder er avsluttet i {{year}} ennå.', { year })
      : missing.length
      ? t('ap.agent.reconcile.desc', '{{closed}} av {{total}} avsluttede måneder er avstemt og bokført.', { closed, total })
      : t('ap.agent.reconcile.descDone', 'Driftsbanken er avstemt til og med forrige måned.'),
    detail: t('ap.agent.reconcile.detail', 'En måned regnes som avstemt når driftsutskriften er bokført og arkivert i Bank. Avstemming må gjøres kronologisk.'),
    // En paagaaende maaned har ingen avsluttet periode aa godkjenne. Den er
    // avventende fram til maanedsslutt, ikke en tom eller godkjent kontroll.
    status: apTaskStatus({ waiting: total === 0, reviewCount: missing.length, done: total > 0 && missing.length === 0 }),
    statusDetail: total === 0 ? t('ap.agent.reconcile.waitingReason', 'Venter på månedsslutt') : '',
    route: 'bank',
    actionLabel: t('ap.agent.reconcile.action', 'Åpne bank'),
    reviewCount: missing.length,
    lines: missing.slice(0, AP_LINE_CAP).map((index) => apLine({
      id: `reconcile-${year}-${index}`,
      title: `${AP_AGENT_MONTHS[index].slice(0, 1).toUpperCase()}${AP_AGENT_MONTHS[index].slice(1)} ${year}`,
      sub: t('ap.agent.reconcile.line', 'Ingen avstemt driftsutskrift'),
      // 'kontroll', ikke 'mangler': filteret for 'mangler' heter «Mangler bilag»
      // i linjemodalen, og det gir ingen mening for en maaned eller en kjoretur.
      status: 'kontroll',
      route: 'bank',
    })),
    kind: 'avstemming',
    scope: 'month',
  });
};

// ---------------------------------------------------------------- kontroll

// To kontroller en regnskapsfører gjør manuelt hver periode: mangler det bilag,
// og er noe ført to ganger. Dobbeltføring defineres strengt — samme leverandør,
// samme beløp, samme dato — for å unngå falske treff på faste månedsutgifter.
const apDuplicateExpenses = (expenses) => {
  const groups = new Map();
  expenses.forEach((expense) => {
    const supplier = String(expense.supplierName || expense.description || '').trim().toLowerCase();
    const amount = Math.round(Number(expense.amountNet) * 100) / 100;
    if (!supplier || !amount || !expense.date) return;
    const key = `${supplier}|${amount}|${expense.date}`;
    groups.set(key, [...(groups.get(key) || []), expense]);
  });
  return [...groups.values()].filter((group) => group.length > 1);
};

const apQualityTask = ({ state, homeModel, year, month, transitionMonth }) => {
  const helpers = apAgentHelpers();
  const { apTask, apLine, apArray, apNumber, apTaskStatus, AP_LINE_CAP = 500 } = helpers;
  if (apAgentImportedPeriod(month, transitionMonth, { state, year })) {
    return apAgentArchiveTask(helpers, { id: 'kontroll', icon: 'alertCircle', kind: 'kontroll', route: 'allebilag', month, year });
  }
  const expenses = apArray(state?.expenses)
    .filter((expense) => expense.status !== 'annulled' && expense.status !== 'draft')
    .filter((expense) => apAgentInPeriod(expense.date, year, month));
  // Hjem-modellen teller bilagsmangler for hele året. Med en valgt måned må
  // tallet komme fra kjøpene selv, og da brukes samme predikat som der.
  const missingPredicate = window.apPeriodHelpers?.apPeriodMissingAttachment;
  // Fri postering fra bank er dokumentert av bankutskriften og er ikke et kjop uten bilag.
  const bankDocumentedIds = window.PlummePostingDocumentation?.bankDocumentedPostingIds(state);
  const missingExpenses = missingPredicate ? expenses.filter((expense) => missingPredicate(expense, bankDocumentedIds)) : [];
  const missingAttachments = month == null || !missingPredicate
    ? apNumber(homeModel?.followup?.missingAttachmentCount)
    : missingExpenses.length;
  const missingVat = month == null ? apNumber(homeModel?.followup?.missingAttachmentVat) : 0;
  const duplicates = apDuplicateExpenses(expenses);
  const findings = missingAttachments + duplicates.length;
  return apTask({
    id: 'kontroll',
    icon: 'alertCircle',
    title: findings
      ? t('ap.agent.quality.title', '{{count}} funn i kvalitetskontrollen', { count: findings })
      : expenses.length
      ? t('ap.agent.quality.titleDone', 'Ingen funn i kvalitetskontrollen')
      : t('ap.agent.quality.titleEmpty', 'Ingen kjøp i {{year}}', { year: apAgentPeriodStrong(year, month) }),
    desc: findings
      ? t('ap.agent.quality.desc', '{{missing}} kjøp mangler bilag og {{duplicates}} kan være ført to ganger.', { missing: missingAttachments, duplicates: duplicates.length })
      : expenses.length
      ? t('ap.agent.quality.descDone', 'Alle kjøp i {{year}} har bilag, og ingen mulige dobbeltføringer er funnet.', { year: apAgentPeriodLabel(year, month) })
      : t('ap.agent.quality.descEmpty', 'Ingen kjøp er registrert i Plumme for perioden, så det er ingenting å kontrollere.'),
    detail: missingVat
      ? t('ap.agent.quality.detailVat', 'Kjøp uten bilag står for {{vat}} kr i MVA-fradrag som kan bli underkjent ved kontroll.', { vat: apAgentAmount(missingVat) })
      : t('ap.agent.quality.detail', 'Mulige dobbeltføringer er kjøp med samme leverandør, beløp og dato. De må vurderes manuelt før noe annulleres.'),
    status: apTaskStatus({ reviewCount: findings, done: findings === 0, checked: expenses.length }),
    route: 'allebilag',
    actionLabel: t('ap.agent.quality.action', 'Åpne transaksjoner'),
    reviewCount: findings,
    lines: [...missingExpenses.map((expense) => apLine({
      id: `missing-${expense.id}`,
      date: expense.date,
      title: expense.supplierName || expense.description || t('ap.agent.quality.purchase', 'Kjøp'),
      sub: t('ap.agent.quality.lineMissing', 'Mangler bilag · åpne kjøpet for å laste opp eller koble dokumentasjon'),
      amount: apNumber(expense.amountNet),
      status: 'kontroll',
      route: 'allebilag', data: { voucherId: expense.id },
    })), ...duplicates.map((group) => apLine({
      id: `dup-${group[0].id}`,
      date: group[0].date,
      title: group[0].supplierName || group[0].description || t('ap.agent.quality.purchase', 'Kjøp'),
      sub: t('ap.agent.quality.lineDuplicate', '{{count}} føringer med samme leverandør, beløp og dato', { count: group.length }),
      amount: apNumber(group[0].amountNet),
      status: 'avvik',
      route: 'allebilag', data: { voucherId: group[0].id },
    }))].slice(0, AP_LINE_CAP),
    kind: 'kontroll',
    scope: 'month',
  });
};

// ---------------------------------------------------------------- frister

const apMileageTask = ({ state, year, month }) => {
  const { apTask, apLine, apArray, apNumber, apTaskStatus, AP_LINE_CAP = 500 } = apAgentHelpers();
  const entries = apArray(state?.mileageEntries)
    .filter((entry) => entry.status !== 'annulled')
    .filter((entry) => entry.usageType !== 'private' && entry.privateUse !== true)
    .filter((entry) => apAgentInPeriod(entry.date, year, month));
  const incomplete = entries.filter((entry) =>
    !String(entry.purpose || '').trim() ||
    !String(entry.fromLocation || '').trim() ||
    !String(entry.toLocation || '').trim() ||
    apNumber(entry.kilometers) <= 0);
  const deduction = entries.reduce((sum, entry) => sum + apNumber(entry.amountNet), 0);
  return apTask({
    id: 'kjorebok',
    icon: 'car',
    title: incomplete.length
      ? t('ap.agent.mileage.title', '{{count}} kjørebokturer mangler dokumentasjon', { count: incomplete.length })
      : entries.length
      ? t('ap.agent.mileage.titleOk', '{{count}} kjørebokturer er dokumentert', { count: entries.length })
      : t('ap.agent.mileage.titleEmpty', 'Ingen kjøring registrert i {{year}}', { year: apAgentPeriodStrong(year, month) }),
    desc: entries.length
      ? t('ap.agent.mileage.desc', 'Kjøreboken gir {{amount}} kr i fradrag i {{year}}.', { amount: apAgentAmount(deduction), year: apAgentPeriodLabel(year, month) })
      : t('ap.agent.mileage.descEmpty', 'Yrkeskjøring gir fradrag, men bare når turene er ført med dato, strekning, formål og kilometer.'),
    detail: t('ap.agent.mileage.detail', 'En kjørebok må vise dato, fra og til, formål og antall kilometer for hver tur. Turer uten dette kan bli underkjent.'),
    status: apTaskStatus({ reviewCount: incomplete.length, done: entries.length > 0 && incomplete.length === 0 }),
    route: 'kjorebok',
    actionLabel: t('ap.agent.mileage.action', 'Åpne kjørebok'),
    reviewCount: incomplete.length,
    lines: incomplete.slice(0, AP_LINE_CAP).map((entry) => apLine({
      id: entry.id,
      date: entry.date,
      title: [entry.fromLocation, entry.toLocation].filter(Boolean).join(' - ') || t('ap.agent.mileage.trip', 'Tur uten strekning'),
      sub: !String(entry.purpose || '').trim()
        ? t('ap.agent.mileage.lineNoPurpose', 'Mangler formål')
        : apNumber(entry.kilometers) <= 0
        ? t('ap.agent.mileage.lineNoKm', 'Mangler kilometer')
        : t('ap.agent.mileage.lineNoRoute', 'Mangler strekning'),
      amount: apNumber(entry.amountNet),
      status: 'kontroll',
      route: 'kjorebok',
      data: {
        date: entry.date,
        fromLocation: entry.fromLocation || '',
        toLocation: entry.toLocation || '',
        purpose: entry.purpose || '',
        kilometers: apNumber(entry.kilometers),
      },
    })),
    kind: 'kjorebok',
    scope: 'month',
  });
};

// ------------------------------------------------------------ bilagsmatch

// Banklinjer som mangler dokumentasjon, sett mot bilag som ligger i innboksen.
//
// NB: antallet treff regnes IKKE ut her. Matchereglene (belop, datovindu og
// tosidig entydighet) ligger i Functions, og en kopi i klienten ville divergert.
// Denne tasken viser grunnlaget; backend avgjor hva som faktisk kan kobles.
const apReceiptMatchTask = ({ state, bankModel, inboxModel, year, today, month, transitionMonth }) => {
  const helpers = apAgentHelpers();
  const { apTask, apLine, apArray, apNumber, apTaskStatus, AP_LINE_CAP = 500 } = helpers;
  if (apAgentImportedPeriod(month, transitionMonth, { state, year, bankOnly: true })) {
    return apAgentArchiveTask(helpers, { id: 'bilagsmatch', icon: 'link', kind: 'bilagsmatch', route: 'bank', month, year });
  }
  if (apAgentBeforeBankCoverage(month, { state, year })) {
    return apAgentBankNotStartedTask(helpers, { id: 'bilagsmatch', icon: 'link', kind: 'bilagsmatch', month, year, from: window.apBankCoverageFrom?.(state) });
  }
  if (!apAgentHasBankReconciliationScope(state)) {
    return apTask({
      id: 'bilagsmatch',
      icon: 'link',
      title: t('ap.agent.match.disabledTitle', 'Bilagsmatch er ikke aktiv'),
      desc: t('ap.agent.match.disabledDesc', 'Ingen aktive bankkontoer er satt til avstemming.'),
      detail: t('ap.agent.match.disabledDetail', 'Når foretaket bruker privat konto uten bankavstemming, finnes det ingen driftsutskrift å koble bilag mot.'),
      status: apTaskStatus(),
      route: 'bank',
      actionLabel: t('ap.agent.match.action', 'Åpne bank'),
      kind: 'bilagsmatch',
      scope: 'month',
    });
  }
  const bankCovered = month != null && apAgentBankCovered(bankModel, year, month);
  if (month != null && !bankCovered && window.apPeriodIsOpen?.(year, month, today)) {
    return apTask({
      id: 'bilagsmatch',
      icon: 'link',
      title: t('ap.agent.match.titleWaiting', '{{month}} er ikke avsluttet ennå', { month: apAgentMonthStrong(month, year) }),
      desc: t('ap.agent.match.descWaiting', 'Bilagsagenten starter bankkontrollen når måneden er avsluttet og bankutskriften kan importeres.'),
      detail: t('ap.agent.match.detailWaiting', 'Ingen bankutskrift mangler ennå. Oppgaven blir aktuell etter månedsslutt.'),
      status: apTaskStatus({ waiting: true }),
      statusDetail: t('ap.agent.reconcile.waitingReason', 'Venter på månedsslutt'),
      route: 'bank',
      kind: 'bilagsmatch',
      scope: 'month',
    });
  }
  if (month != null && !bankCovered) {
    return apAgentAwaitingStatement(helpers, { id: 'bilagsmatch', icon: 'link', kind: 'bilagsmatch', month, year, accounts: apAgentAccountsMissingStatement(state, bankModel, year, month) });
  }
  const statements = apArray(bankModel?.statements).filter((statement) =>
    statement.mode === 'drift' && apAgentStatementCovers(statement, year, month));
  const activeStatements = statements.filter((statement) => statement.status === 'active');
  const missing = activeStatements.flatMap((statement) => apArray(statement.lineItems)
    .filter((line) => apNumber(line.amount) < 0 && line.bilag)
    .filter((line) => apAgentInPeriod(line.date, year, month))
    .map((line) => ({ ...line, statement })));
  const available = apArray(inboxModel?.active).length;
  const bankLines = statements.reduce((sum, statement) => sum + apArray(statement.lineItems).length, 0);
  const canMatch = missing.length > 0 && available > 0;
  return apTask({
    id: 'bilagsmatch',
    icon: 'link',
    title: missing.length
      ? t('ap.agent.match.title', '{{count}} banklinjer mangler bilag', { count: missing.length })
      : bankLines
      ? t('ap.agent.match.titleDone', 'Alle banklinjer har bilag')
      : t('ap.agent.match.titleEmpty', 'Ingen banklinjer i {{year}}', { year: apAgentPeriodStrong(year, month) }),
    desc: missing.length
      ? t('ap.agent.match.desc', '{{count}} bilag ligger i innboksen og kan passe.', { count: available })
      : bankLines
      ? t('ap.agent.match.descDone', 'Ingen banklinjer mangler dokumentasjon.')
      : t('ap.agent.match.descEmpty', 'Ingen bankutskrift dekker perioden, så det er ingenting å koble.'),
    detail: t('ap.agent.match.detail', 'Et bilag kobles automatisk bare når det passer én banklinje og banklinjen passer ett bilag. Er det tvil, må du velge selv.'),
    status: apTaskStatus({ reviewCount: missing.length, done: missing.length === 0, checked: bankLines }),
    route: 'bank',
    actionLabel: t('ap.agent.match.action', 'Åpne bank'),
    reviewCount: missing.length,
    lines: missing.slice(0, AP_LINE_CAP).map((line) => apLine({
      id: line.id,
      date: line.date,
      title: line.title,
      sub: t('ap.agent.match.line', 'Mangler bilag'),
      amount: apNumber(line.amount),
      status: 'mangler',
      route: 'bank',
    })),
    execution: canMatch ? {
      action: 'match-inbox-to-bank',
      count: missing.length,
      label: t('ap.agent.match.execute', 'Koble bilag automatisk'),
      confirmTitle: t('ap.agent.match.executeTitle', 'Koble bilag mot banklinjer'),
      confirmWarn: t('ap.agent.match.executeWarn', 'Bilagene kobles som dokumentasjon på banklinjene. Ingenting bokføres — det skjer først når du avstemmer utskriften.'),
      checkedNote: t('ap.agent.match.executeNote', 'Bare entydige treff kobles: bilaget må passe én banklinje, og banklinjen ett bilag. Alt annet blir stående til manuelt valg.'),
      doneLabel: t('ap.agent.match.executeDone', 'bilag koblet'),
    } : null,
    kind: 'bilagsmatch',
    scope: 'month',
  });
};

// ------------------------------------------------------------- kontakter

// Motparter som gaar igjen i regnskapet uten aa finnes i registeret.
//
// Antallet her er et grovt anslag: stoyfilteret (som luker bort «Visa», «Vipps»
// og rene tall) og entydighetsreglene ligger i Functions. Knappeteksten lover
// derfor ikke et antall.
const apContactTask = ({ state, type, year, month }) => {
  const { apTask, apLine, apArray, apTaskStatus, AP_LINE_CAP = 500 } = apAgentHelpers();
  const isSupplier = type === 'supplier';
  const counts = new Map();
  const rows = isSupplier
    ? apArray(state?.expenses)
      .filter((entry) => entry.status !== 'annulled' && entry.status !== 'draft' && !entry.supplierId)
      // Leverandørregisteret er for faktiske leverandørforhold. Kvitteringer
      // fra butikk, drivstoff og servering skal ikke lage kontaktarbeid.
      .filter((entry) => entry.isSupplierInvoice === true)
      .map((entry) => entry.supplierName)
    : apArray(state?.invoices)
      .filter((entry) => entry.status !== 'draft' && !entry.customerId && !entry.customer?.id)
      .map((entry) => entry.customer?.name);
  rows.forEach((name) => {
    const key = String(name || '').trim().toLocaleLowerCase('nb-NO');
    if (key.length < 3) return;
    counts.set(key, { name: String(name).trim(), count: (counts.get(key)?.count || 0) + 1 });
  });
  const taskId = isSupplier ? 'leverandorer' : 'kunder';
  const repeated = [...counts.entries()].map(([key, entry]) => ({ ...entry, key }))
    .filter((entry) => entry.count > 1)
    .filter((entry) => !window.apAutopilotItemHidden?.(state, taskId, `${type}:${encodeURIComponent(entry.key)}`, year, month))
    .sort((a, b) => b.count - a.count);
  const noun = isSupplier ? t('ap.agent.contacts.supplier', 'leverandører') : t('ap.agent.contacts.customer', 'kunder');
  return apTask({
    id: taskId,
    icon: isSupplier ? 'building' : 'users',
    title: repeated.length
      ? t('ap.agent.contacts.title', 'Inntil {{count}} {{noun}} mangler i registeret', { count: repeated.length, noun })
      : t('ap.agent.contacts.titleDone', 'Alle {{noun}} er registrert', { noun }),
    desc: repeated.length
      ? t('ap.agent.contacts.desc', 'Navn som går igjen i regnskapet uten å ligge i registeret.')
      : t('ap.agent.contacts.descDone', 'Ingen gjentakende navn utenfor registeret.'),
    detail: t('ap.agent.contacts.detail', 'Bare navn og organisasjonsnummer opprettes. E-post, telefon og adresse må du fylle inn selv — Plumme gjetter ikke på kontaktdata som kan havne på en faktura.'),
    status: apTaskStatus({ reviewCount: repeated.length, done: repeated.length === 0 }),
    route: isSupplier ? 'leverandorer' : 'kunder',
    actionLabel: isSupplier ? t('ap.agent.contacts.openSuppliers', 'Åpne leverandører') : t('ap.agent.contacts.openCustomers', 'Åpne kunder'),
    reviewCount: repeated.length,
    lines: repeated.slice(0, AP_LINE_CAP).map((entry) => apLine({
      id: `${type}:${encodeURIComponent(entry.key)}`,
      title: entry.name,
      sub: t('ap.agent.contacts.line', '{{count}} føringer', { count: entry.count }),
      status: 'kontroll',
      route: isSupplier ? 'leverandorer' : 'kunder',
    })),
    execution: repeated.length ? {
      action: 'create-missing-contacts',
      count: repeated.length,
      types: [type],
      label: t('ap.agent.contacts.execute', 'Opprett manglende {{noun}}', { noun }),
      confirmTitle: t('ap.agent.contacts.executeTitle', 'Opprett {{noun}} i registeret', { noun }),
      confirmWarn: t('ap.agent.contacts.executeWarn', 'Navnene opprettes i registeret. Eksisterende føringer kobles ikke automatisk — det gjør du når du åpner posten.'),
      checkedNote: t('ap.agent.contacts.executeNote', 'Plumme oppretter bare navn som går igjen minst to ganger, og hopper over betalingstekst som «Visa» og «Vipps».'),
      doneLabel: t('ap.agent.contacts.executeDone', 'opprettet'),
    } : null,
    kind: 'kontakter',
    // Registeret er ikke en periode. Et navn som mangler hører ikke til mars.
    scope: 'year',
  });
};

// ------------------------------------------------ skatt og aarsregnskap

const apYearReadiness = (state, year, route) => {
  const lines = [];
  const closed = Boolean(state?.closedYearsByYear?.[String(year)]);
  // Maanedssignaturen finnes ikke lenger, og «X maaneder er ikke lukket» kan
  // derfor ikke telles av signaturer. Det som faktisk staar igjen i aaret
  // ligger allerede i punktene under — bilag og banklinjer — mens selve
  // aarsavslutningen er `closedYearsByYear`.
  const hasYearData = [...apAgentHelpers().apArray(state?.expenses), ...apAgentHelpers().apArray(state?.invoices), ...apAgentHelpers().apArray(state?.otherSales)]
    .some((entry) => String(entry?.date || entry?.issueDate || '').startsWith(`${year}-`));
  if (hasYearData && !closed) lines.push({ id: 'year-open', title: t('ap.readiness.yearOpen', 'Regnskapsåret er ikke avsluttet'), sub: t('ap.readiness.yearOpenSub', 'Årsavslutningen er ikke gjennomført'), route });
  const pendingReceipts = apAgentHelpers().apArray(state?.receiptInbox).filter((entry) =>
    !['booked', 'trashed'].includes(entry?.status) && String(entry?.receiptAnalysis?.receiptDate || entry?.received || '').startsWith(`${year}-`)).length;
  if (pendingReceipts) lines.push({ id: 'year-inbox', title: t('ap.readiness.inbox', '{{count}} bilag er ikke ferdig behandlet', { count: pendingReceipts }), sub: t('ap.readiness.inboxSub', 'Fullfør innboksen før årsinnsending'), route: 'innboks' });
  const openBank = apAgentHelpers().apArray(state?.bankStatements).flatMap((statement) => apAgentHelpers().apArray(statement?.lines))
    .filter((line) => String(line?.date || '').startsWith(`${year}-`))
    .filter((line) => !line?.matchedEntryId && !['matched', 'booked'].includes(line?.reconciliationStatus)).length;
  if (openBank) lines.push({ id: 'year-bank', title: t('ap.readiness.bank', '{{count}} banklinjer er ikke ferdig avstemt', { count: openBank }), sub: t('ap.readiness.bankSub', 'Fullfør bankavstemmingen'), route: 'bank' });
  const previous = state?.deadlinesByYear?.[String(Number(year) - 1)];
  if (previous && previous.taxSubmitted === false) lines.push({ id: 'previous-tax', title: t('ap.readiness.previousTax', 'Forrige skattemelding er ikke bekreftet levert'), sub: t('ap.readiness.reviewYear', 'Kontroller {{year}}', { year: Number(year) - 1 }), route: 'skattemelding' });
  return lines;
};

// Skattemeldingen. Ingen utforing: innsending gaar via Altinn, som ikke er
// koblet paa i produksjon. Tasken viser hva som faktisk ligger til grunn, slik
// at agentbryteren ikke styrer en tom rad.
const apTaxTask = ({ taxModel, year, state }) => {
  const { apTask, apLine, apNumber, apTaskStatus } = apAgentHelpers();
  if (!taxModel) {
    return apTask({
      id: 'skatt',
      icon: 'doc',
      title: t('ap.agent.tax.titleBlocked', 'Skattegrunnlaget er ikke tilgjengelig'),
      desc: t('ap.agent.tax.descBlocked', 'Regnskapstallene for {{year}} er ikke lastet.', { year }),
      detail: t('ap.agent.tax.detail', 'Innsending av skattemelding krever Altinn-integrasjon, som ikke er koblet på ennå.'),
      status: apTaskStatus({ blocked: true }),
      route: 'skattemelding',
      blockedReason: t('ap.agent.tax.blocked', 'Mangler regnskapstall'),
      kind: 'skatt',
      scope: 'year',
    });
  }
  const summary = taxModel.summary || {};
  const submitted = Boolean(taxModel.submission?.submittedAt);
  const prerequisites = submitted ? [] : apYearReadiness(state, year, 'skattemelding');
  return apTask({
    id: 'skatt',
    icon: 'doc',
    title: submitted
      ? t('ap.agent.tax.titleDone', 'Skattemeldingen for {{year}} er levert', { year })
      : prerequisites.length
      ? t('ap.agent.tax.titleMissing', '{{count}} forutsetninger mangler før skattemeldingen', { count: prerequisites.length })
      : t('ap.agent.tax.title', 'Skattegrunnlaget for {{year}} er beregnet', { year }),
    desc: taxModel.isEnk
      ? t('ap.agent.tax.descEnk', 'Resultat før skatt: {{result}} kr. Enkeltpersonforetak skattlegges på eierens hånd.', { result: apAgentAmount(summary.resultBeforeTax) })
      : t('ap.agent.tax.desc', 'Resultat før skatt {{result}} kr gir {{tax}} kr i beregnet skatt.', { result: apAgentAmount(summary.resultBeforeTax), tax: apAgentAmount(summary.taxAmount) }),
    detail: t('ap.agent.tax.detail', 'Innsending av skattemelding krever Altinn-integrasjon, som ikke er koblet på ennå.'),
    status: apTaskStatus({ reviewCount: submitted ? 0 : Math.max(1, prerequisites.length), done: submitted }),
    route: 'skattemelding',
    actionLabel: t('ap.agent.tax.action', 'Åpne skattemelding'),
    reviewCount: submitted ? 0 : Math.max(1, prerequisites.length),
    lines: [
      ...prerequisites.map((entry) => apLine({ ...entry, status: 'kontroll' })),
      apLine({ id: 'tax-result', title: t('ap.agent.tax.lineResult', 'Resultat før skatt'), amount: apNumber(summary.resultBeforeTax), status: 'matchet', route: 'skattemelding' }),
      !taxModel.isEnk && apLine({ id: 'tax-amount', title: t('ap.agent.tax.lineTax', 'Beregnet skatt'), sub: `${Math.round((summary.taxRate || 0) * 100)} %`, amount: apNumber(summary.taxAmount), status: 'matchet', route: 'skattemelding' }),
      apNumber(summary.lossCarryforward) > 0 && apLine({ id: 'tax-loss', title: t('ap.agent.tax.lineLoss', 'Underskudd til framføring'), amount: apNumber(summary.lossCarryforward), status: 'kontroll', route: 'skattemelding' }),
    ].filter(Boolean),
    kind: 'skatt',
    scope: 'year',
  });
};

// Aarsregnskapet. Bare AS og liknende har plikt; ENK har ingen aarsregnskap aa
// levere, og da skal agenten si det i stedet for aa vise en tom oppgave.
const apAnnualAccountsTask = ({ annualModel, year, state }) => {
  const { apTask, apLine, apArray, apTaskStatus } = apAgentHelpers();
  if (!annualModel) {
    return apTask({
      id: 'arsregnskap',
      icon: 'files',
      title: t('ap.agent.annual.titleBlocked', 'Årsregnskapet er ikke tilgjengelig'),
      desc: t('ap.agent.annual.descBlocked', 'Regnskapstallene for {{year}} er ikke lastet.', { year }),
      status: apTaskStatus({ blocked: true }),
      route: 'arsregnskap',
      blockedReason: t('ap.agent.annual.blocked', 'Mangler regnskapstall'),
      kind: 'arsregnskap',
      scope: 'year',
    });
  }
  // Annual accounts are not a customer workflow for an ordinary Norwegian ENK.
  // Do not turn a non-applicable/unsupported filing into a completed agent task.
  if (annualModel.annualAccountsRequired === false) return null;
  const status = annualModel.status || {};
  const process = annualModel.process || {};
  const steps = [
    ['annualAccountsDocumentPrepared', t('ap.agent.annual.stepDocument', 'Årsregnskapsdokumentet')],
    ['boardProtocolPrepared', t('ap.agent.annual.stepBoard', 'Styreprotokoll')],
    ['generalAssemblyProtocolPrepared', t('ap.agent.annual.stepAssembly', 'Generalforsamlingsprotokoll')],
    ['annualAccountsSigned', t('ap.agent.annual.stepSigned', 'Signering')],
  ];
  const remaining = steps.filter(([key]) => !process[key]);
  const prerequisites = status.submitted ? [] : apYearReadiness(state, year, 'arsregnskap');
  return apTask({
    id: 'arsregnskap',
    icon: 'files',
    title: status.submitted
      ? t('ap.agent.annual.titleDone', 'Årsregnskapet for {{year}} er levert', { year })
      : prerequisites.length
      ? t('ap.agent.annual.titleMissing', '{{count}} forutsetninger mangler før årsregnskapet', { count: prerequisites.length })
      : remaining.length
      ? t('ap.agent.annual.title', '{{count}} steg gjenstår i årsregnskapet for {{year}}', { count: remaining.length, year })
      : t('ap.agent.annual.titleReady', 'Årsregnskapet for {{year}} er klart til innsending', { year }),
    desc: status.yearClosed
      ? t('ap.agent.annual.descClosed', 'Regnskapsåret er avsluttet.')
      : t('ap.agent.annual.descOpen', 'Regnskapsåret er ikke avsluttet ennå.'),
    detail: t('ap.agent.annual.detail', 'Innsending til Regnskapsregisteret krever Altinn-integrasjon, som ikke er koblet på ennå.'),
    status: apTaskStatus({ reviewCount: status.submitted ? 0 : prerequisites.length + remaining.length, done: status.submitted }),
    route: 'arsregnskap',
    actionLabel: t('ap.agent.annual.action', 'Åpne årsregnskap'),
    reviewCount: status.submitted ? 0 : prerequisites.length + remaining.length,
    lines: [...prerequisites.map((entry) => apLine({ ...entry, status: 'kontroll' })), ...apArray(remaining).map(([key, label]) => apLine({
      id: key,
      title: label,
      sub: t('ap.agent.annual.line', 'Ikke fullført'),
      status: 'kontroll',
      route: 'arsregnskap',
    }))],
    kind: 'arsregnskap',
    scope: 'year',
  });
};

// Rekkefølgen er arbeidsrekkefølgen: få bilagene inn, få pengene inn, avstem,
// kontroller, og hold fristene.
function buildAutopilotAgentTasks(context = {}) {
  if (!window.apTaskHelpers) return [];
  return [
    apInboxTask(context),
    apReceiptMatchTask(context),
    apInvoiceTask(context),
    apDunningTask(context),
    apReconciliationTask(context),
    apContactTask({ ...context, type: 'supplier' }),
    apContactTask({ ...context, type: 'customer' }),
    apQualityTask(context),
    apTaxTask(context),
    apAnnualAccountsTask(context),
    window.apDeadlineTask(context),
    window.apAnnualDeadlineMonthTask?.(context),
    apMileageTask(context),
    // Fristraden finnes bare i månedene noe faktisk forfaller i.
  ].filter(Boolean);
}

// `autopilot-model.jsx` bygger bank- og MVA-oppgaven, og de maa ha nøyaktig
// samme overtakelsessperre som agentene her. Deles i stedet for aa kopieres, saa
// de to ikke kan gli fra hverandre.
Object.assign(window, { buildAutopilotAgentTasks, apAgentImportedPeriod, apAgentArchiveTask, apAgentBeforeBankCoverage, apAgentBankNotStartedTask, apAgentDaysBetween, apAgentParseDate, AP_AGENT_MONTHS });
